import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * The race state over a results page hero, with its category, on one line:
 * START IN 3d 18:48:41 — SUMMIT FUN TRAIL 21K, ONGOING 01:02:03 — …, FINISHED — ….
 * Bottom right on a wide screen; between both edges on a phone, where the text
 * shrinks just enough to fit the image instead of running past it and being
 * cut (the longest name so far: SAVANA TRAIL 10K TNI/POLRI).
 *
 * state: "before" | "ongoing" | "finished"
 */
const MAX_PX = 18;
const MIN_PX = 10;

export const HeroStatus = ({ state, time, category }) => {
  const label = state === "before" ? `START IN ${time}` : state === "ongoing" ? `ONGOING ${time}` : "FINISHED";
  const text = `${label} — ${category}`;
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const [px, setPx] = useState(MAX_PX);

  // The largest size, MAX_PX down, at which the line fits the box. Measured on
  // the category and the width, not every second: the clock's digits keep the
  // same width (tabular-nums), and "3d" → "" only ever shortens it.
  const fitKey = `${state}|${category}|${String(time).length}`;
  useLayoutEffect(() => {
    const fit = () => {
      const box = boxRef.current, el = textRef.current;
      if (!box || !el) return;
      let size = MAX_PX;
      el.style.fontSize = `${size}px`;
      while (size > MIN_PX && el.scrollWidth > box.clientWidth) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
      setPx(size);
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (boxRef.current) ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, [fitKey]);

  return (
    <div ref={boxRef} className="absolute inset-x-3 bottom-2 z-10 text-white drop-shadow-md md:inset-x-auto md:left-auto md:bottom-4 md:right-4 md:text-right">
      <span ref={textRef} className="inline-block max-w-full whitespace-nowrap leading-tight tabular-nums" style={{ fontSize: `${px}px` }}>
        {text}
      </span>
    </div>
  );
};
